import type {
  EmployeePublic,
  EmployeeDetailResponse,
  EmployeeListResponse,
  DocumentationStatus,
  RequiredDocuments,
  DashboardAlertsResponse,
  EmployeeDocument,
  ApiError,
  TaskCodeWithCurrentRate,
  TaskCodeListResponse,
  TaskCodeDetailResponse,
  CreateTaskCodeRequest,
  UpdateTaskCodeRequest,
  AddRateRequest,
  TaskCodeListParams,
  TaskCodeRate,
  Rate,
  RateListResponse,
  RateDetailResponse,
  RateVersionsResponse,
  CreateRateRequest,
  AddRateVersionRequest,
  RefreshRateRequest,
  RateRefreshLogResponse,
  RateRefreshPreviewResponse,
  RateRefreshLogParams,
  TimesheetWithEntries,
  TimesheetListResponse,
  TimesheetListParams,
  TimesheetEntry,
  CreateEntryRequest,
  UpdateEntryRequest,
  TimesheetTotals,
  WeekInfo,
  WeeklyTarget,
  FundListResponse,
  FundSyncResponse,
  Volunteer,
  VolunteerListResponse,
  VolunteerDetailResponse,
  VolunteerListParams,
  CreateVolunteerRequest,
  UpdateVolunteerRequest,
  VolunteerDocumentListResponse,
  VolunteerDocumentView,
  CreateVolunteerStandingAttestationRequest,
  BatchTranscriptionResponse,
  BulkCreateVolunteerTimeEntriesRequest,
  BulkCreateVolunteerTimeEntriesResponse,
  CreateVolunteerTimeEntryRequest,
  ListVolunteerTimeEntriesParams,
  ListVolunteerTimeEntriesResponse,
  ResolvedRate,
  SigninSheetBatchRow,
  UpdateVolunteerTimeEntryRequest,
  VolunteerTimeEntry,
  VolunteerTimeEntryResponse,
  VoidVolunteerTimeEntryRequest,
  ApproveVolunteerEntryRequest,
  ApproveVolunteerEntryResponse,
  RequestVolunteerEntryChangesRequest,
  VolunteerApprovalQueueParams,
  VolunteerApprovalQueueResponse,
  VolunteerEntryForApproval,
  Grant,
  GrantListResponse,
  GrantListParams,
  GrantTransitionResult,
  CreateGrantInput,
  UpdateGrantMetadataInput,
  TransitionGrantStatusInput,
  MatchBankEntry,
  ProposeEntriesRequest,
  ProposeEntriesResponse,
  VoidAllocationRequest,
  AllocationFailure,
  AllocationState,
  AuditLogEntry,
  MatchBankSummary,
  VolunteerWorkflowStatus,
  Asu202007Summary,
  FilingSourceData,
  RateRefreshLogRow,
  GrantClaimSummary,
  VolunteerTranscript,
} from '@renewal/types';

const API_BASE = '/api';

/**
 * Custom error for API calls with enhanced diagnostics
 */
export class ApiRequestError extends Error {
  public readonly isTimeout: boolean;
  public readonly endpoint: string;
  public readonly timestamp: string;
  public readonly diagnosticInfo: string;
  public readonly responseBody: Record<string, unknown>;

  constructor(
    message: string,
    public status: number,
    public code?: string,
    endpoint?: string,
    responseBody?: Record<string, unknown>
  ) {
    super(message);
    this.name = 'ApiRequestError';
    this.endpoint = endpoint || 'unknown';
    this.timestamp = new Date().toISOString();
    this.isTimeout = status === 504 || code === 'FUNCTION_INVOCATION_TIMEOUT';
    this.responseBody = responseBody || {};

    // Build diagnostic string for easy debugging
    this.diagnosticInfo = [
      `Status: ${status}`,
      `Endpoint: ${this.endpoint}`,
      `Time: ${this.timestamp}`,
      code ? `Code: ${code}` : null,
      this.isTimeout ? 'Type: SERVERLESS_TIMEOUT' : null,
    ]
      .filter(Boolean)
      .join(' | ');
  }

  /**
   * Get a user-friendly error message
   */
  getUserMessage(): string {
    if (this.isTimeout) {
      return 'The server took too long to respond. This may happen during high load or after periods of inactivity. Please try again in a moment.';
    }
    return this.message;
  }

  /**
   * Log diagnostic info to console for debugging
   */
  logDiagnostics(): void {
    console.error(`[API Error] ${this.diagnosticInfo}`);
    console.error(`[API Error] Message: ${this.message}`);
    if (this.isTimeout) {
      console.error(
        '[API Error] This is a serverless function timeout (10s limit on Vercel Hobby)'
      );
      console.error('[API Error] Consider: retry the request, or check Vercel function logs');
    }
  }
}

/**
 * Check if an error is a timeout error
 */
export function isTimeoutError(error: unknown): error is ApiRequestError {
  return error instanceof ApiRequestError && error.isTimeout;
}

/**
 * Format error for display, with special handling for timeouts
 */
export function formatApiError(error: unknown): string {
  if (error instanceof ApiRequestError) {
    error.logDiagnostics();
    return error.getUserMessage();
  }
  if (error instanceof Error) {
    // Network errors (offline, DNS failure, etc.)
    if (error.message === 'Failed to fetch') {
      return 'Unable to connect to the server. Please check your internet connection.';
    }
    return error.message;
  }
  return 'An unexpected error occurred. Please try again.';
}

/**
 * Get the current auth token from localStorage
 */
function getAuthToken(): string | null {
  return localStorage.getItem('token');
}

/**
 * Set the auth token in localStorage
 */
export function setAuthToken(token: string): void {
  localStorage.setItem('token', token);
}

/**
 * Clear the auth token from localStorage
 */
export function clearAuthToken(): void {
  localStorage.removeItem('token');
}

/**
 * Check if user is authenticated
 */
export function isAuthenticated(): boolean {
  return !!getAuthToken();
}

/**
 * Get CSRF token from cookie
 */
function getCsrfToken(): string | null {
  const match = document.cookie.match(/(?:^|;\s*)_csrf=([^;]*)/);
  return match && match[1] ? decodeURIComponent(match[1]) : null;
}

/**
 * Fetch CSRF token from server (initializes cookie).
 * Call this once on app startup.
 */
export async function initializeCsrfToken(): Promise<void> {
  try {
    await fetch(`${API_BASE}/csrf-token`, {
      credentials: 'include',
    });
  } catch {
    // Silently fail - CSRF protection is optional in development
    console.warn('Failed to initialize CSRF token');
  }
}

/**
 * Make an authenticated API request
 */
export async function apiRequest<T>(endpoint: string, options: RequestInit = {}): Promise<T> {
  const method = options.method?.toUpperCase() || 'GET';
  const isMutating = ['POST', 'PUT', 'PATCH', 'DELETE'].includes(method);

  // Built fresh per attempt so a CSRF token refreshed mid-request (on retry) is
  // picked up.
  const buildHeaders = (): Record<string, string> => {
    const headers: Record<string, string> = {};
    if (options.headers) {
      Object.assign(headers, options.headers as Record<string, string>);
    }
    const token = getAuthToken();
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }
    // Only set Content-Type for non-FormData requests
    if (!(options.body instanceof FormData)) {
      headers['Content-Type'] = 'application/json';
    }
    // Add CSRF token for state-changing requests
    if (isMutating) {
      const csrfToken = getCsrfToken();
      if (csrfToken) {
        headers['X-CSRF-Token'] = csrfToken;
      }
    }
    return headers;
  };

  const doFetch = async (): Promise<Response> => {
    try {
      return await fetch(`${API_BASE}${endpoint}`, {
        ...options,
        headers: buildHeaders(),
        credentials: 'include', // Include cookies for CSRF
      });
    } catch {
      // Network-level errors (offline, DNS, CORS, etc.)
      const apiError = new ApiRequestError('Failed to fetch', 0, 'NETWORK_ERROR', endpoint);
      apiError.logDiagnostics();
      throw apiError;
    }
  };

  let response = await doFetch();

  // Self-heal a missing/stale CSRF cookie. The server plants a fresh `_csrf`
  // cookie on the 403, so we fetch a token (which also refreshes the cookie)
  // and replay the request exactly once. Without this, the first mutation of a
  // session — or any mutation after the 24h cookie expiry — fails with "CSRF
  // token not found" until the user manually refreshes. (See csrf.middleware.ts.)
  if (isMutating && response.status === 403) {
    const peeked = (await response
      .clone()
      .json()
      .catch(() => null)) as ApiError | null;
    if (peeked?.error === 'CSRF_TOKEN_MISSING' || peeked?.error === 'CSRF_TOKEN_INVALID') {
      await initializeCsrfToken();
      response = await doFetch();
    }
  }

  if (!response.ok) {
    // Try to parse error response, but handle timeout specially
    let errorBody: ApiError;
    try {
      errorBody = (await response.json()) as ApiError;
    } catch {
      // For 504 timeouts, Vercel returns plain text, not JSON
      if (response.status === 504) {
        errorBody = {
          error: 'FUNCTION_INVOCATION_TIMEOUT',
          message: 'Request timed out',
        };
      } else {
        errorBody = {
          error: 'Unknown error',
          message: response.statusText,
        };
      }
    }

    const apiError = new ApiRequestError(
      errorBody.message || 'Request failed',
      response.status,
      errorBody.error,
      endpoint,
      errorBody as unknown as Record<string, unknown>
    );
    apiError.logDiagnostics();
    throw apiError;
  }

  // Handle 204 No Content
  if (response.status === 204) {
    return {} as T;
  }

  return response.json() as Promise<T>;
}

// ============================================================================
// Auth API
// ============================================================================

export async function logout(): Promise<void> {
  try {
    await apiRequest('/auth/logout', { method: 'POST' });
  } finally {
    clearAuthToken();
  }
}

export async function getCurrentUser(): Promise<{ employee: EmployeePublic }> {
  return apiRequest('/auth/me');
}

// ============================================================================
// Employee API
// ============================================================================

export async function getEmployees(params?: {
  status?: 'active' | 'archived' | 'all';
  search?: string;
}): Promise<EmployeeListResponse> {
  const searchParams = new URLSearchParams();
  if (params?.status) searchParams.set('status', params.status);
  if (params?.search) searchParams.set('search', params.search);

  const query = searchParams.toString();
  return apiRequest(`/employees${query ? `?${query}` : ''}`);
}

export async function getEmployee(id: string): Promise<EmployeeDetailResponse> {
  return apiRequest(`/employees/${id}`);
}

// updateEmployee and archiveEmployee removed — employee data managed in app-portal

export async function getEmployeeDocuments(id: string): Promise<{ documents: EmployeeDocument[] }> {
  return apiRequest(`/employees/${id}/documents`);
}

export async function getEmployeeDocumentationStatus(id: string): Promise<{
  documentationStatus: DocumentationStatus;
  requiredDocuments: RequiredDocuments;
}> {
  return apiRequest(`/employees/${id}/documentation-status`);
}

// ============================================================================
// Document API
// ============================================================================

export async function uploadDocument(
  employeeId: string,
  file: File,
  type: 'parental_consent' | 'work_permit' | 'safety_training',
  expiresAt?: string
): Promise<{ document: EmployeeDocument }> {
  const formData = new FormData();
  formData.append('file', file);
  formData.append('type', type);
  if (expiresAt) {
    formData.append('expiresAt', expiresAt);
  }

  return apiRequest(`/employees/${employeeId}/documents`, {
    method: 'POST',
    body: formData,
  });
}

export async function markSafetyTrainingComplete(
  employeeId: string
): Promise<{ message: string; document: EmployeeDocument }> {
  return apiRequest(`/employees/${employeeId}/safety-training`, {
    method: 'POST',
  });
}

export async function getDocument(id: string): Promise<{ document: EmployeeDocument }> {
  return apiRequest(`/documents/${id}`);
}

export async function getDocumentDownloadUrl(
  id: string
): Promise<{ url: string; expiresAt: string }> {
  return apiRequest(`/documents/${id}/download`);
}

export async function invalidateDocument(id: string): Promise<{ message: string }> {
  return apiRequest(`/documents/${id}`, { method: 'DELETE' });
}

// ============================================================================
// Dashboard API
// ============================================================================

export async function getDashboardEmployees(): Promise<EmployeeListResponse> {
  return apiRequest('/dashboard/employees');
}

export async function getDashboardAlerts(): Promise<DashboardAlertsResponse> {
  return apiRequest('/dashboard/alerts');
}

export async function getDashboardStats(): Promise<{
  stats: {
    totalEmployees: number;
    completeDocumentation: number;
    missingDocumentation: number;
    expiringDocuments: number;
    pendingReviewCount: number;
    byAgeBand: {
      '12-13': number;
      '14-15': number;
      '16-17': number;
      '18+': number;
    };
  };
}> {
  return apiRequest('/dashboard/stats');
}

// ============================================================================
// Task Code API
// ============================================================================

export async function getTaskCodes(params?: TaskCodeListParams): Promise<TaskCodeListResponse> {
  const searchParams = new URLSearchParams();
  if (params?.isAgricultural) searchParams.set('isAgricultural', params.isAgricultural);
  if (params?.isHazardous) searchParams.set('isHazardous', params.isHazardous);
  if (params?.isVolunteer) searchParams.set('isVolunteer', params.isVolunteer);
  if (params?.forAge !== undefined) searchParams.set('forAge', params.forAge.toString());
  if (params?.includeInactive) searchParams.set('includeInactive', params.includeInactive);
  if (params?.search) searchParams.set('search', params.search);

  const query = searchParams.toString();
  return apiRequest(`/task-codes${query ? `?${query}` : ''}`);
}

export async function getTaskCode(id: string): Promise<TaskCodeDetailResponse> {
  return apiRequest(`/task-codes/${id}`);
}

export async function getTaskCodeByCode(
  code: string
): Promise<{ taskCode: TaskCodeWithCurrentRate }> {
  return apiRequest(`/task-codes/by-code/${code}`);
}

export async function createTaskCode(
  data: CreateTaskCodeRequest
): Promise<{ taskCode: TaskCodeWithCurrentRate }> {
  return apiRequest('/task-codes', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function updateTaskCode(
  id: string,
  data: UpdateTaskCodeRequest
): Promise<{ taskCode: TaskCodeWithCurrentRate }> {
  return apiRequest(`/task-codes/${id}`, {
    method: 'PATCH',
    body: JSON.stringify(data),
  });
}

export async function addTaskCodeRate(
  id: string,
  data: AddRateRequest
): Promise<{ rate: TaskCodeRate }> {
  return apiRequest(`/task-codes/${id}/rates`, {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function getTaskCodeRates(id: string): Promise<{ rates: TaskCodeRate[] }> {
  return apiRequest(`/task-codes/${id}/rates`);
}

export async function getTaskCodesForEmployee(
  employeeId: string,
  workDate?: string
): Promise<TaskCodeListResponse> {
  const params = workDate ? `?workDate=${workDate}` : '';
  return apiRequest(`/task-codes/for-employee/${employeeId}${params}`);
}

// ============================================================================
// Rates API (Phase 2: Rates Engine — admin only for writes)
// ============================================================================

export async function getRates(): Promise<RateListResponse> {
  return apiRequest('/rates');
}

export async function getRate(id: string): Promise<RateDetailResponse> {
  return apiRequest(`/rates/${id}`);
}

export async function getRateVersions(id: string): Promise<RateVersionsResponse> {
  return apiRequest(`/rates/${id}/versions`);
}

export async function createRate(data: CreateRateRequest): Promise<{ rate: Rate }> {
  return apiRequest('/rates', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function addRateVersion(
  id: string,
  data: AddRateVersionRequest
): Promise<{ rate: Rate }> {
  return apiRequest(`/rates/${id}/versions`, {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function refreshRate(
  id: string,
  data: RefreshRateRequest
): Promise<{ rate: Rate; priorValue: string | null }> {
  return apiRequest(`/rates/${id}/refresh`, {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

/**
 * Phase 12c Task 3 (R17.3) — read-only impact preview for the rate-refresh
 * wizard's step 4. `newValue` is optional (omit to preview against the current
 * resolved value).
 */
export async function previewRateRefresh(
  rateId: string,
  rateYear: number,
  newValue?: string
): Promise<RateRefreshPreviewResponse> {
  const sp = new URLSearchParams({ rateYear: String(rateYear) });
  if (newValue != null && newValue !== '') sp.set('newValue', newValue);
  return apiRequest(`/rates/${rateId}/refresh-preview?${sp.toString()}`);
}

export async function getRateRefreshLog(
  params?: RateRefreshLogParams
): Promise<RateRefreshLogResponse> {
  const searchParams = new URLSearchParams();
  if (params?.rateId) searchParams.set('rateId', params.rateId);
  if (params?.rateYear !== undefined) searchParams.set('rateYear', params.rateYear.toString());
  if (params?.from) searchParams.set('from', params.from);
  if (params?.to) searchParams.set('to', params.to);

  const query = searchParams.toString();
  return apiRequest(`/rates/refresh-log${query ? `?${query}` : ''}`);
}

// ============================================================================
// Timesheet API
// ============================================================================

/**
 * Get list of employee's timesheets.
 */
export async function getTimesheets(params?: TimesheetListParams): Promise<TimesheetListResponse> {
  const searchParams = new URLSearchParams();
  if (params?.status) searchParams.set('status', params.status);
  if (params?.limit !== undefined) searchParams.set('limit', params.limit.toString());
  if (params?.offset !== undefined) searchParams.set('offset', params.offset.toString());

  const query = searchParams.toString();
  return apiRequest(`/timesheets${query ? `?${query}` : ''}`);
}

/**
 * Get or create timesheet for the current week.
 */
export async function getCurrentTimesheet(): Promise<TimesheetWithEntries> {
  return apiRequest('/timesheets/current');
}

/**
 * Get or create timesheet for a specific week.
 */
export async function getTimesheetByWeek(weekStartDate: string): Promise<TimesheetWithEntries> {
  return apiRequest(`/timesheets/week/${weekStartDate}`);
}

/**
 * Get timesheet by ID with entries.
 */
export async function getTimesheetById(id: string): Promise<TimesheetWithEntries> {
  return apiRequest(`/timesheets/${id}`);
}

/**
 * Create a new entry on a timesheet.
 */
export async function createTimesheetEntry(
  timesheetId: string,
  entry: CreateEntryRequest
): Promise<{ entry: TimesheetEntry }> {
  return apiRequest(`/timesheets/${timesheetId}/entries`, {
    method: 'POST',
    body: JSON.stringify(entry),
  });
}

/**
 * Bulk create response type.
 */
export interface BulkCreateEntriesResponse {
  entries: TimesheetEntry[];
  created: number;
  failed: number;
  errors: Array<{ index: number; error: string }>;
}

/**
 * Create multiple entries on a timesheet at once.
 * Used for multi-day drag operations in timeline UI.
 */
export async function createTimesheetEntriesBulk(
  timesheetId: string,
  entries: CreateEntryRequest[]
): Promise<BulkCreateEntriesResponse> {
  return apiRequest(`/timesheets/${timesheetId}/entries/bulk`, {
    method: 'POST',
    body: JSON.stringify({ entries }),
  });
}

/**
 * Update an entry on a timesheet.
 */
export async function updateTimesheetEntry(
  timesheetId: string,
  entryId: string,
  updates: UpdateEntryRequest
): Promise<{ entry: TimesheetEntry }> {
  return apiRequest(`/timesheets/${timesheetId}/entries/${entryId}`, {
    method: 'PATCH',
    body: JSON.stringify(updates),
  });
}

/**
 * Delete an entry from a timesheet.
 */
export async function deleteTimesheetEntry(timesheetId: string, entryId: string): Promise<void> {
  return apiRequest(`/timesheets/${timesheetId}/entries/${entryId}`, {
    method: 'DELETE',
  });
}

/**
 * Export timesheet entries as CSV.
 * Returns the CSV content as a Blob for download.
 */
export async function exportTimesheetEntries(timesheetId: string): Promise<Blob> {
  const token = getAuthToken();
  const headers: Record<string, string> = {};

  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }

  const response = await fetch(`${API_BASE}/timesheets/${timesheetId}/entries/export`, {
    method: 'GET',
    headers,
    credentials: 'include',
  });

  if (!response.ok) {
    const error = (await response.json().catch(() => ({
      error: 'Unknown error',
      message: response.statusText,
    }))) as ApiError;

    throw new ApiRequestError(error.message || 'Export failed', response.status, error.error);
  }

  return response.blob();
}

/**
 * Get totals for a timesheet.
 */
export async function getTimesheetTotals(timesheetId: string): Promise<TimesheetTotals> {
  return apiRequest(`/timesheets/${timesheetId}/totals`);
}

/**
 * Get week information for a timesheet.
 */
export async function getTimesheetWeekInfo(timesheetId: string): Promise<WeekInfo> {
  return apiRequest(`/timesheets/${timesheetId}/week-info`);
}

/**
 * Compliance violation from the API.
 */
export interface ComplianceViolation {
  ruleId: string;
  ruleName: string;
  message: string;
  remediation: string;
  affectedDates?: string[];
  affectedEntries?: string[];
}

/**
 * Result of submitting a timesheet.
 */
export interface SubmitTimesheetResult {
  passed: boolean;
  message?: string;
  status?: string;
  violations?: ComplianceViolation[];
  summary?: {
    total: number;
    passed: number;
    failed: number;
    notApplicable: number;
  };
  complianceSummary?: {
    total: number;
    passed: number;
    notApplicable: number;
  };
  /** The week against 40 hours after the submit (also on 400/409 refusal bodies). */
  weeklyTarget?: WeeklyTarget;
}

/**
 * Submit a timesheet for compliance check and review.
 */
export async function submitTimesheet(timesheetId: string): Promise<SubmitTimesheetResult> {
  try {
    return await apiRequest(`/timesheets/${timesheetId}/submit`, {
      method: 'POST',
    });
  } catch (error) {
    // Handle compliance check failures specially
    if (error instanceof ApiRequestError && error.status === 400) {
      // The response body should contain the compliance errors
      // Re-throw with parsed violations if present
      throw error;
    }
    throw error;
  }
}

// ============================================================================
// Entry Compliance Preview API (for Timeline UI Phase 3)
// ============================================================================

import type { EntryPreviewRequest, EntryCompliancePreview } from '@renewal/types';

/**
 * Preview compliance for a proposed entry without saving.
 * Returns violations, warnings, limits, and requirements.
 */
export async function previewEntryCompliance(
  timesheetId: string,
  entry: EntryPreviewRequest
): Promise<EntryCompliancePreview> {
  return apiRequest(`/timesheets/${timesheetId}/entries/preview`, {
    method: 'POST',
    body: JSON.stringify(entry),
  });
}

// ============================================================================
// Supervisor Review API
// ============================================================================

import type {
  ReviewQueueItem,
  TimesheetReviewData,
  ApproveTimesheetResponse,
  RejectTimesheetResponse,
  UnlockWeekRequest,
  UnlockWeekResponse,
  ComplianceCheckLog,
} from '@renewal/types';

/**
 * Review queue response.
 */
export interface ReviewQueueResponse {
  items: ReviewQueueItem[];
  total: number;
}

/**
 * Get list of timesheets awaiting review.
 */
export async function getReviewQueue(params?: {
  employeeId?: string;
}): Promise<ReviewQueueResponse> {
  const searchParams = new URLSearchParams();
  if (params?.employeeId) searchParams.set('employeeId', params.employeeId);

  const query = searchParams.toString();
  return apiRequest(`/supervisor/review-queue${query ? `?${query}` : ''}`);
}

/**
 * Get count of timesheets pending review.
 */
export async function getPendingReviewCount(): Promise<{ count: number }> {
  return apiRequest('/supervisor/review-count');
}

/**
 * Get a timesheet for supervisor review with full details.
 */
export async function getTimesheetForReview(timesheetId: string): Promise<TimesheetReviewData> {
  return apiRequest(`/supervisor/review/${timesheetId}`);
}

/**
 * Get compliance logs for a timesheet.
 */
export async function getComplianceLogs(
  timesheetId: string
): Promise<{ logs: ComplianceCheckLog[] }> {
  return apiRequest(`/supervisor/review/${timesheetId}/compliance`);
}

/**
 * Approve a submitted timesheet.
 */
export async function approveTimesheet(
  timesheetId: string,
  notes?: string
): Promise<ApproveTimesheetResponse> {
  return apiRequest(`/supervisor/review/${timesheetId}/approve`, {
    method: 'POST',
    body: JSON.stringify({ notes }),
  });
}

/**
 * Reject a submitted timesheet.
 */
export async function rejectTimesheet(
  timesheetId: string,
  notes: string
): Promise<RejectTimesheetResponse> {
  return apiRequest(`/supervisor/review/${timesheetId}/reject`, {
    method: 'POST',
    body: JSON.stringify({ notes }),
  });
}

/**
 * Reopen an approved week for an employee. The reason (at least 10 characters)
 * is required and logged.
 */
export async function unlockWeek(
  employeeId: string,
  weekStartDate: string,
  reason: string
): Promise<UnlockWeekResponse> {
  const body: UnlockWeekRequest = { employeeId, weekStartDate, reason };
  return apiRequest('/supervisor/unlock-week', {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

// ============================================================================
// Payroll API
// ============================================================================

import type {
  PayrollReportResponse,
  PayrollRecordResponse,
  PayrollRecalculateResponse,
  ApproveTimesheetWithPayrollResponse,
} from '@renewal/types';

/**
 * Get payroll record for a specific timesheet.
 */
export async function getPayrollRecord(timesheetId: string): Promise<PayrollRecordResponse> {
  return apiRequest(`/payroll/timesheet/${timesheetId}`);
}

/**
 * Age band type for filtering.
 */
export type AgeBand = '12-13' | '14-15' | '16-17' | '18+';

/**
 * Get payroll report with filters.
 */
export async function getPayrollReport(params: {
  startDate: string;
  endDate: string;
  employeeId?: string;
  ageBand?: AgeBand;
}): Promise<PayrollReportResponse> {
  const searchParams = new URLSearchParams();
  searchParams.set('startDate', params.startDate);
  searchParams.set('endDate', params.endDate);
  if (params.employeeId) searchParams.set('employeeId', params.employeeId);
  if (params.ageBand) searchParams.set('ageBand', params.ageBand);

  return apiRequest(`/payroll/report?${searchParams.toString()}`);
}

/**
 * Export payroll records as CSV.
 * Returns the CSV content as a Blob for download.
 */
export async function exportPayrollCSV(params: {
  startDate: string;
  endDate: string;
  employeeId?: string;
  ageBand?: AgeBand;
}): Promise<Blob> {
  const token = getAuthToken();
  const headers: Record<string, string> = {};

  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }
  headers['Content-Type'] = 'application/json';

  // Add CSRF token for POST request
  const csrfToken = getCsrfToken();
  if (csrfToken) {
    headers['X-CSRF-Token'] = csrfToken;
  }

  const response = await fetch(`${API_BASE}/payroll/export`, {
    method: 'POST',
    headers,
    credentials: 'include',
    body: JSON.stringify(params),
  });

  if (!response.ok) {
    const error = (await response.json().catch(() => ({
      error: 'Unknown error',
      message: response.statusText,
    }))) as ApiError;

    throw new ApiRequestError(error.message || 'Export failed', response.status, error.error);
  }

  return response.blob();
}

/**
 * Recalculate payroll for an approved timesheet.
 */
export async function recalculatePayroll(timesheetId: string): Promise<PayrollRecalculateResponse> {
  return apiRequest(`/payroll/recalculate/${timesheetId}`, {
    method: 'POST',
  });
}

/**
 * Approve timesheet and get payroll result.
 * This is an enhanced version of approveTimesheet that includes payroll info.
 */
export async function approveTimesheetWithPayroll(
  timesheetId: string,
  notes?: string
): Promise<ApproveTimesheetWithPayrollResponse> {
  return apiRequest(`/supervisor/review/${timesheetId}/approve`, {
    method: 'POST',
    body: JSON.stringify({ notes }),
  });
}

// ============================================================================
// Funds (financial-system integration)
// ============================================================================

// ============================================================================
// Financial Status API (staging sync)
// ============================================================================

export interface FinancialStatusRecord {
  id: string;
  sourceRecordId: string;
  fundId: number;
  /**
   * The fund's display name, resolved server-side.
   *
   * Render this, never `fundId`. The client does not own the rule for what a
   * fund id means — an absent fund is General Fund, not "unassigned", and
   * restating that here is how it drifted last time. The server sends the
   * finished label; there is nothing to fall back to.
   */
  fundLabel: string;
  amount: string;
  status: string;
  syncedAt: string;
  lastCheckedAt: string | null;
  metadata: unknown;
}

export interface TimesheetFinancialStatusResponse {
  timesheetId: string;
  records: FinancialStatusRecord[];
  allPosted: boolean;
}

/**
 * Get financial system staging status for a timesheet.
 */
export async function getTimesheetFinancialStatus(
  timesheetId: string,
  refresh = false
): Promise<TimesheetFinancialStatusResponse> {
  const params = refresh ? '?refresh=true' : '';
  return apiRequest(`/timesheets/${timesheetId}/financial-status${params}`);
}

// ============================================================================
// Funds (financial-system integration)
// ============================================================================

/**
 * Cached funds. By default only funds open to new hours; `includeAll` returns
 * every cached fund with its `selectable` flag.
 */
export async function getFunds(options: { includeAll?: boolean } = {}): Promise<FundListResponse> {
  return apiRequest(options.includeAll ? '/funds?include=all' : '/funds');
}

export async function syncFunds(): Promise<FundSyncResponse> {
  return apiRequest('/funds/sync', { method: 'POST' });
}

// ============================================================================
// Volunteer API (Phase 3: Volunteer & Admin Management — admin only)
// ============================================================================

export async function getVolunteers(params?: VolunteerListParams): Promise<VolunteerListResponse> {
  const searchParams = new URLSearchParams();
  if (params?.status) searchParams.set('status', params.status);
  if (params?.tier) searchParams.set('tier', params.tier);
  if (params?.search) searchParams.set('search', params.search);

  const query = searchParams.toString();
  return apiRequest(`/volunteers${query ? `?${query}` : ''}`);
}

export async function getVolunteer(id: string): Promise<VolunteerDetailResponse> {
  return apiRequest(`/volunteers/${id}`);
}

export async function createVolunteer(
  data: CreateVolunteerRequest
): Promise<{ volunteer: Volunteer }> {
  return apiRequest('/volunteers', { method: 'POST', body: JSON.stringify(data) });
}

export async function updateVolunteer(
  id: string,
  data: UpdateVolunteerRequest
): Promise<{ volunteer: Volunteer }> {
  return apiRequest(`/volunteers/${id}`, { method: 'PATCH', body: JSON.stringify(data) });
}

export async function archiveVolunteer(id: string): Promise<{ volunteer: Volunteer }> {
  return apiRequest(`/volunteers/${id}/archive`, { method: 'POST' });
}

export async function getVolunteerDocuments(id: string): Promise<VolunteerDocumentListResponse> {
  return apiRequest(`/volunteers/${id}/documents`);
}

export async function uploadVolunteerDocument(
  volunteerId: string,
  file: File,
  documentType: string
): Promise<{ document: VolunteerDocumentView }> {
  const formData = new FormData();
  formData.append('file', file);
  formData.append('documentType', documentType);
  return apiRequest(`/volunteers/${volunteerId}/documents`, { method: 'POST', body: formData });
}

/**
 * In-app standing-attestation sign-off — the alternative to uploading a file.
 * Posts the typed name + PNG signature data URL as JSON; the server renders and
 * stores a `volunteer_attestation_standing` PDF and returns the linked document.
 */
export async function attestVolunteerStanding(
  volunteerId: string,
  payload: CreateVolunteerStandingAttestationRequest
): Promise<{ document: VolunteerDocumentView }> {
  return apiRequest(`/volunteers/${volunteerId}/attestation`, {
    method: 'POST',
    body: JSON.stringify(payload),
  });
}

export async function getVolunteerDocumentDownloadUrl(
  docId: string
): Promise<{ url: string; expiresAt: string }> {
  return apiRequest(`/volunteer-documents/${docId}/download`);
}

export async function invalidateVolunteerDocument(
  docId: string
): Promise<{ document: VolunteerDocumentView }> {
  return apiRequest(`/volunteer-documents/${docId}`, { method: 'DELETE' });
}

// ============================================================================
// Volunteer Time Entries (Phase 4 — R1, R2)
// ============================================================================

/** The current user's volunteer record (R15.6). Throws 404 when none exists. */
export async function getMyVolunteer(): Promise<{ volunteer: Volunteer }> {
  return apiRequest('/volunteers/me');
}

/** Resolve the rate version applicable to a service date (preview). */
export async function resolveRateForDate(
  rateId: string,
  date: string
): Promise<{ resolved: ResolvedRate }> {
  return apiRequest(`/rates/${rateId}/resolve?date=${encodeURIComponent(date)}`);
}

/** Create a skilled-tier self-entry (R1). */
export async function createVolunteerTimeEntry(
  data: CreateVolunteerTimeEntryRequest
): Promise<VolunteerTimeEntryResponse> {
  return apiRequest('/volunteer-time-entries', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

/**
 * Bulk-create skilled-tier self-entries from the week grid (1–20). The rate is
 * resolved server-side per entry (no `rateId` in the request — R5.8). Returns
 * partial-success counts + per-entry errors.
 */
export async function bulkCreateVolunteerTimeEntries(
  data: BulkCreateVolunteerTimeEntriesRequest
): Promise<BulkCreateVolunteerTimeEntriesResponse> {
  return apiRequest('/volunteer-time-entries/bulk', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

/** List volunteer time entries (own scope for non-admin; full for admin). */
export async function listVolunteerTimeEntries(
  params?: ListVolunteerTimeEntriesParams
): Promise<ListVolunteerTimeEntriesResponse> {
  const search = new URLSearchParams();
  if (params?.volunteerId) search.set('volunteerId', params.volunteerId);
  if (params?.status) search.set('status', params.status);
  if (params?.from) search.set('from', params.from);
  if (params?.to) search.set('to', params.to);
  if (params?.limit !== undefined) search.set('limit', params.limit.toString());
  const query = search.toString();
  return apiRequest(`/volunteer-time-entries${query ? `?${query}` : ''}`);
}

/**
 * Get one volunteer time entry, with any supervisor revision-request comments
 * surfaced from the audit log (Phase 5 Task 4).
 */
export async function getVolunteerTimeEntry(id: string): Promise<{
  entry: VolunteerTimeEntry;
  supervisorRequestComments: import('@renewal/types').VolunteerEntryRequestComment[];
}> {
  return apiRequest(`/volunteer-time-entries/${id}`);
}

/** Update a pre-approval draft entry (R1.6). */
export async function updateVolunteerTimeEntry(
  id: string,
  data: UpdateVolunteerTimeEntryRequest
): Promise<VolunteerTimeEntryResponse> {
  return apiRequest(`/volunteer-time-entries/${id}`, {
    method: 'PATCH',
    body: JSON.stringify(data),
  });
}

/** Void a pre-approval entry (R14.6). */
export async function voidVolunteerTimeEntry(
  id: string,
  data: VoidVolunteerTimeEntryRequest
): Promise<{ entry: VolunteerTimeEntry }> {
  return apiRequest(`/volunteer-time-entries/${id}/void`, {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

/** Admin batch transcription from a paper sign-in scan (R2). */
export async function transcribeSigninSheet(
  file: File,
  rows: SigninSheetBatchRow[]
): Promise<BatchTranscriptionResponse> {
  const formData = new FormData();
  formData.append('file', file);
  formData.append('rows', JSON.stringify(rows));
  return apiRequest('/volunteer-time-entries/batch-from-signin-sheet', {
    method: 'POST',
    body: formData,
  });
}

// ============================================================================
// Volunteer Approval (Phase 5 — admin only)
// ============================================================================

/** List entries awaiting supervisor approval (FIFO). */
export async function listVolunteerApprovalQueue(
  params?: VolunteerApprovalQueueParams
): Promise<VolunteerApprovalQueueResponse> {
  const search = new URLSearchParams();
  if (params?.volunteerId) search.set('volunteerId', params.volunteerId);
  if (params?.taskCode) search.set('taskCode', params.taskCode);
  if (params?.from) search.set('from', params.from);
  if (params?.to) search.set('to', params.to);
  const query = search.toString();
  return apiRequest(`/volunteer-approval/queue${query ? `?${query}` : ''}`);
}

/** Read the full approval-detail view of one pending entry. */
export async function getVolunteerEntryForApproval(
  entryId: string
): Promise<VolunteerEntryForApproval> {
  return apiRequest(`/volunteer-approval/${entryId}`);
}

/** Approve a pending entry (classify + transition + audit). Phase 11 owns GL. */
export async function approveVolunteerEntry(
  entryId: string,
  payload: ApproveVolunteerEntryRequest
): Promise<ApproveVolunteerEntryResponse> {
  return apiRequest(`/volunteer-approval/${entryId}/approve`, {
    method: 'POST',
    body: JSON.stringify(payload),
  });
}

/** Request changes on a pending entry — comment only, no status change. */
export async function requestVolunteerEntryChanges(
  entryId: string,
  payload: RequestVolunteerEntryChangesRequest
): Promise<void> {
  await apiRequest(`/volunteer-approval/${entryId}/request-changes`, {
    method: 'POST',
    body: JSON.stringify(payload),
  });
}

// ============================================================================
// Grants (Phase 6 — admin only)
// ============================================================================

export async function listGrantsApi(params?: GrantListParams): Promise<GrantListResponse> {
  const search = new URLSearchParams();
  if (params?.status) search.set('status', params.status);
  if (params?.funderType) search.set('funderType', params.funderType);
  if (params?.periodFrom) search.set('periodFrom', params.periodFrom);
  if (params?.periodTo) search.set('periodTo', params.periodTo);
  if (params?.excludeCancelled) search.set('excludeCancelled', 'true');
  const query = search.toString();
  return apiRequest(`/grants${query ? `?${query}` : ''}`);
}

export async function getGrantApi(id: string): Promise<{ grant: Grant }> {
  return apiRequest(`/grants/${id}`);
}

export async function createGrantApi(payload: CreateGrantInput): Promise<{ grant: Grant }> {
  return apiRequest('/grants', { method: 'POST', body: JSON.stringify(payload) });
}

export async function updateGrantMetadataApi(
  id: string,
  payload: UpdateGrantMetadataInput
): Promise<{ grant: Grant }> {
  return apiRequest(`/grants/${id}`, { method: 'PATCH', body: JSON.stringify(payload) });
}

export async function transitionGrantStatusApi(
  id: string,
  payload: TransitionGrantStatusInput
): Promise<GrantTransitionResult> {
  return apiRequest(`/grants/${id}/transition`, {
    method: 'POST',
    body: JSON.stringify(payload),
  });
}

// ============================================================================
// Match-bank + Allocations (Phase 6 — admin only)
// ============================================================================

export interface MatchBankResponse {
  items: MatchBankEntry[];
  total: number;
  sumMatchUsd: string;
  sumHours: string;
}

export interface MatchBankParams {
  states?: AllocationState[];
  grantId?: string;
  taskCode?: string;
  from?: string;
  to?: string;
}

export interface MatchBankSummaryParams {
  releasedFrom?: string;
  releasedTo?: string;
  fy?: number;
  volunteerId?: string;
  taskCode?: string;
  grantId?: string;
}

export async function getMatchBankSummaryApi(
  params?: MatchBankSummaryParams
): Promise<MatchBankSummary> {
  const search = new URLSearchParams();
  if (params?.releasedFrom) search.set('releasedFrom', params.releasedFrom);
  if (params?.releasedTo) search.set('releasedTo', params.releasedTo);
  if (params?.fy !== undefined) search.set('fy', String(params.fy));
  if (params?.volunteerId) search.set('volunteerId', params.volunteerId);
  if (params?.taskCode) search.set('taskCode', params.taskCode);
  if (params?.grantId) search.set('grantId', params.grantId);
  const query = search.toString();
  return apiRequest(`/match-bank/summary${query ? `?${query}` : ''}`);
}

/**
 * Guided admin home ("Start here") workflow-status counts. Admin-only.
 */
export async function getVolunteerWorkflowStatusApi(): Promise<VolunteerWorkflowStatus> {
  const res = await apiRequest<{ status: VolunteerWorkflowStatus }>(
    '/dashboard/volunteer-workflow-status'
  );
  return res.status;
}

// ============================================================================
// Phase 9a — Reports (R12.*)
// ============================================================================

export async function getAsu202007Api(fy: number): Promise<Asu202007Summary> {
  return apiRequest(`/reports/asu-2020-07?fy=${fy}`);
}

export function asu202007CsvUrl(fy: number): string {
  return `${API_BASE}/reports/asu-2020-07.csv?fy=${fy}`;
}

export async function getFilingSourceDataApi(fy: number): Promise<FilingSourceData> {
  return apiRequest(`/reports/filing-source-data?fy=${fy}`);
}

export interface RateRefreshLogReportParams {
  rateId?: string;
  rateYear?: number;
  from?: string;
  to?: string;
}

export async function getRateRefreshLogReportApi(
  params?: RateRefreshLogReportParams
): Promise<{ rows: RateRefreshLogRow[] }> {
  const search = new URLSearchParams();
  if (params?.rateId) search.set('rateId', params.rateId);
  if (params?.rateYear !== undefined) search.set('rateYear', String(params.rateYear));
  if (params?.from) search.set('from', params.from);
  if (params?.to) search.set('to', params.to);
  const query = search.toString();
  return apiRequest(`/reports/rate-refresh-log${query ? `?${query}` : ''}`);
}

export function rateRefreshLogCsvUrl(params?: RateRefreshLogReportParams): string {
  const search = new URLSearchParams();
  if (params?.rateId) search.set('rateId', params.rateId);
  if (params?.rateYear !== undefined) search.set('rateYear', String(params.rateYear));
  if (params?.from) search.set('from', params.from);
  if (params?.to) search.set('to', params.to);
  const q = search.toString();
  return `${API_BASE}/reports/rate-refresh-log.csv${q ? `?${q}` : ''}`;
}

export async function getGrantClaimSummaryApi(grantId: string): Promise<GrantClaimSummary> {
  return apiRequest(`/grants/${grantId}/claim-summary`);
}

// --- Volunteer transcripts (Phase 9c) ----------------------------------

export async function getVolunteerTranscriptApi(
  id: string,
  period?: string
): Promise<VolunteerTranscript> {
  const query = period ? `?period=${encodeURIComponent(period)}` : '';
  return apiRequest(`/volunteers/${id}/transcript${query}`);
}

export async function sendVolunteerTranscriptApi(
  id: string,
  body: { period: string; force?: boolean }
): Promise<{ sent: boolean; skipped: boolean; error?: string }> {
  return apiRequest(`/volunteers/${id}/transcript`, {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

export function grantClaimSummaryCsvUrl(grantId: string): string {
  return `${API_BASE}/grants/${grantId}/claim-summary.csv`;
}

export function grantClaimSummaryPdfUrl(grantId: string): string {
  return `${API_BASE}/grants/${grantId}/claim-summary.pdf`;
}

/** B2 / H-2 — freeze a grant's locked entries (mark the claim as reported). */
export async function markGrantClaimReported(
  grantId: string,
  reason?: string
): Promise<{ frozen: number }> {
  return apiRequest(`/grants/${grantId}/claim/mark-reported`, {
    method: 'POST',
    body: JSON.stringify(reason ? { reason } : {}),
  });
}

// --- Audit packages (Phase 9b Task 4) ----------------------------------

import type { AuditPackageManifest, AuditPackageRecord } from '@renewal/types';

export interface GenerateAuditPackageResponse {
  documentId: string;
  blobUrl: string;
  manifest: AuditPackageManifest;
  sizeBytes: number;
}

export async function listAuditPackagesApi(): Promise<{ packages: AuditPackageRecord[] }> {
  return apiRequest('/audit-packages');
}

export async function generateAuditPackageApi(
  fiscalYear: number
): Promise<GenerateAuditPackageResponse> {
  return apiRequest('/audit-packages', {
    method: 'POST',
    body: JSON.stringify({ fiscalYear }),
  });
}

export async function getAuditPackageManifestApi(
  documentId: string
): Promise<{ manifest: AuditPackageManifest }> {
  return apiRequest(`/audit-packages/${documentId}/manifest`);
}

export async function getMatchBankApi(params?: MatchBankParams): Promise<MatchBankResponse> {
  const search = new URLSearchParams();
  if (params?.states && params.states.length > 0) {
    search.set('states', params.states.join(','));
  }
  if (params?.grantId) search.set('grantId', params.grantId);
  if (params?.taskCode) search.set('taskCode', params.taskCode);
  if (params?.from) search.set('from', params.from);
  if (params?.to) search.set('to', params.to);
  const query = search.toString();
  return apiRequest(`/match-bank${query ? `?${query}` : ''}`);
}

export async function proposeEntriesApi(
  payload: ProposeEntriesRequest
): Promise<ProposeEntriesResponse> {
  return apiRequest('/allocations/propose', {
    method: 'POST',
    body: JSON.stringify(payload),
  });
}

export async function unproposeEntriesApi(entryIds: string[]): Promise<{
  unallocated: import('@renewal/types').VolunteerTimeEntry[];
  failed: AllocationFailure[];
}> {
  return apiRequest('/allocations/unpropose', {
    method: 'POST',
    body: JSON.stringify({ entryIds }),
  });
}

export async function voidAllocationApi(
  entryId: string,
  payload: VoidAllocationRequest
): Promise<{ entry: import('@renewal/types').VolunteerTimeEntry }> {
  return apiRequest(`/allocations/entries/${entryId}/void`, {
    method: 'POST',
    body: JSON.stringify(payload),
  });
}

export async function getEntryAllocationHistoryApi(
  entryId: string
): Promise<{ history: AuditLogEntry[] }> {
  return apiRequest(`/allocations/entries/${entryId}/history`);
}

// ============================================================================
// Kiosk admin (Phase 7 — admin waiver mgmt + completion queue)
// ============================================================================

export async function listKioskWaiversApi(): Promise<
  import('@renewal/types').KioskWaiverVersionListResponse
> {
  return apiRequest('/kiosk-admin/waivers');
}

export async function getCurrentKioskWaiverApi(): Promise<
  import('@renewal/types').KioskWaiverDocument
> {
  return apiRequest('/kiosk-admin/waivers/current');
}

export async function publishKioskWaiverApi(
  payload: import('@renewal/types').PublishKioskWaiverRequest
): Promise<import('@renewal/types').KioskWaiverDocument> {
  return apiRequest('/kiosk-admin/waivers', {
    method: 'POST',
    body: JSON.stringify(payload),
  });
}

export async function listKioskPendingCompletionApi(): Promise<
  import('@renewal/types').KioskPendingCompletionListResponse
> {
  return apiRequest('/kiosk-admin/pending-completion');
}

export async function completeKioskEntryApi(
  entryId: string,
  payload: import('@renewal/types').CompleteKioskEntryRequest
): Promise<import('@renewal/types').CompleteKioskEntryResponse> {
  return apiRequest(`/kiosk-admin/entries/${entryId}/complete`, {
    method: 'POST',
    body: JSON.stringify(payload),
  });
}

// ============================================================================
// Phase 8 — Audit log + fiscal periods (R14.7, R14.8)
// ============================================================================

import type { FiscalPeriod, PreCloseReadiness, FiscalPeriodCloseRequest } from '@renewal/types';

export interface AuditLogListResponse {
  rows: AuditLogEntry[];
  nextCursor: string | null;
}

export interface AuditLogFilters {
  from?: string;
  to?: string;
  entityType?: string;
  entityId?: string;
  actorId?: string;
  action?: string;
  limit?: number;
  cursor?: string;
}

export async function listAuditLogApi(
  filters: AuditLogFilters = {}
): Promise<AuditLogListResponse> {
  const q = new URLSearchParams();
  if (filters.from) q.set('from', filters.from);
  if (filters.to) q.set('to', filters.to);
  if (filters.entityType) q.set('entityType', filters.entityType);
  if (filters.entityId) q.set('entityId', filters.entityId);
  if (filters.actorId) q.set('actorId', filters.actorId);
  if (filters.action) q.set('action', filters.action);
  if (filters.limit) q.set('limit', String(filters.limit));
  if (filters.cursor) q.set('cursor', filters.cursor);
  const qs = q.toString();
  return apiRequest(`/audit-log${qs ? `?${qs}` : ''}`);
}

/** Returns the URL the browser should hit to start the CSV download. The
 *  server streams the response; the browser saves via Content-Disposition. */
export function auditLogCsvUrl(filters: AuditLogFilters = {}): string {
  const q = new URLSearchParams();
  if (filters.from) q.set('from', filters.from);
  if (filters.to) q.set('to', filters.to);
  if (filters.entityType) q.set('entityType', filters.entityType);
  if (filters.entityId) q.set('entityId', filters.entityId);
  if (filters.actorId) q.set('actorId', filters.actorId);
  if (filters.action) q.set('action', filters.action);
  const qs = q.toString();
  return `${API_BASE}/audit-log/csv${qs ? `?${qs}` : ''}`;
}

export async function listFiscalPeriodsApi(
  status?: 'open' | 'closed'
): Promise<{ items: FiscalPeriod[] }> {
  const qs = status ? `?status=${status}` : '';
  return apiRequest(`/fiscal-periods${qs}`);
}

export async function getFiscalPeriodApi(id: string): Promise<{ period: FiscalPeriod }> {
  return apiRequest(`/fiscal-periods/${id}`);
}

export async function getPreCloseReadinessApi(
  id: string
): Promise<{ readiness: PreCloseReadiness }> {
  return apiRequest(`/fiscal-periods/${id}/pre-close-readiness`);
}

export async function closeFiscalPeriodApi(
  id: string,
  body: FiscalPeriodCloseRequest
): Promise<{ period: FiscalPeriod }> {
  return apiRequest(`/fiscal-periods/${id}/close`, {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

// ============================================================================
// Volunteer GL push (Phase 11a — admin "Processor" surface)
// ============================================================================

export interface GlQueuePreviewItem {
  entryId: string;
  volunteerFullName: string;
  dateOfService: string;
  hoursWorked: string;
  criterion: string;
  projectedAmount: string | null;
}

export interface GlFailedItem {
  glTransactionId: string;
  entryId: string | null;
  amount: string;
  transactionType: string;
  createdAt: string;
}

export interface GlPushResult {
  success: boolean;
  reason?: string;
  attempted?: number;
  succeeded?: number;
  alreadyPosted?: number;
  failed?: { entryId: string; code: string; message: string }[];
}

export async function getGlQueueApi(): Promise<{ items: GlQueuePreviewItem[]; total: number }> {
  return apiRequest('/admin/gl/queue');
}

export async function getGlFailedApi(): Promise<{ items: GlFailedItem[]; total: number }> {
  return apiRequest('/admin/gl/failed');
}

export async function pushGlQueueApi(): Promise<GlPushResult> {
  return apiRequest('/admin/gl/push', {
    method: 'POST',
    body: JSON.stringify({ scope: 'queue' }),
  });
}
